import { useState, useEffect, useRef } from "react";
import { Eye, EyeSlash } from "@phosphor-icons/react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContextBackend";
import { isValidRedirect } from "../utils/redirect";
import { isMainDomain, buildSubdomainUrl, getBaseDomain, getCurrentSubdomain } from "../utils/subdomain";
import { setAccessToken as setStoredAccessToken, setRefreshToken as setStoredRefreshToken, getAccessToken, getRefreshToken } from "../utils/tokenStorage";

// Build a URL that hands off tokens to the target subdomain via URL fragment.
// Fragments are not sent to the server, so they don't bloat headers or appear in logs.
function withTokenHandoff(url: string): string {
  const access = getAccessToken();
  const refresh = getRefreshToken();
  if (!access) return url;
  const frag = `access_token=${encodeURIComponent(access)}${refresh ? `&refresh_token=${encodeURIComponent(refresh)}` : ''}`;
  return `${url}#${frag}`;
}
import { Button } from "@t-one-internal/design-system";
import { Input } from "@t-one-internal/design-system";
import { Separator } from "@t-one-internal/design-system";
import OtpVerificationModal from "./OtpVerificationModal";
import TwoFactorSetupModal from "./TwoFactorSetupModal";
import { Shield } from "@phosphor-icons/react";
import { resendEmailOTP } from "../services/twoFactorApi";
import { extractUserClaims } from "../utils/jwtUtils";
import logo from "../assets/logo.png";
import performsparkLogo from "../assets/Performspark.png";
import traineryLearnLogo from "../assets/TraineryLEARN.jpg";
import compBldrLogo from "../assets/CompBldr.svg";

// Helper function to detect if input is email or userId
const isEmail = (value: string): boolean => {
  return value.includes('@');
};

// Helper function to validate email format
const isValidEmail = (email: string): boolean => {
  // Check for spaces
  if (email.includes(' ')) {
    return false;
  }
  // Email validation regex - requires min 2-char TLD
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  return emailRegex.test(email);
};

// Helper function to determine redirect path
const getRedirectPathForUser = (_accessToken: string): string => {
  return '/home/my-portal';
};

export default function LoginForm() {
  const { loginWithGoogle, loginWithMicrosoft, loginWithCredentials, adminCheckAuth, adminLoginWithCredentials, verifyOtpWithSession, checkAuthMethod } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const redirectParam = searchParams.get("redirect") ?? undefined;
  const [email, setEmail] = useState("");
  const [secondIdentifier, setSecondIdentifier] = useState(""); // For BOTH case
  const [isAdminFlow, setIsAdminFlow] = useState(false); // Admin login flow
  const [adminUserId, setAdminUserId] = useState(""); // Admin User ID field
  const [adminDetectedByUserId, setAdminDetectedByUserId] = useState(false); // Admin detected via admin_user_id (not email)
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSecondIdentifier, setShowSecondIdentifier] = useState(false); // For BOTH access_by
  const [showOrgId, setShowOrgId] = useState(false); // For Employee Code collision
  const [orgId, setOrgId] = useState("");
  const [showPassword, setShowPassword] = useState(false); // Controls 2-step flow (field visibility)
  const [passwordVisible, setPasswordVisible] = useState(false); // Controls content visibility (eye icon)
  const [firstInputIsEmail, setFirstInputIsEmail] = useState(false); // Track if first input was email
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const secondIdentifierInputRef = useRef<HTMLInputElement>(null);
  const adminUserIdInputRef = useRef<HTMLInputElement>(null);

  // OTP Modal state
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpSessionId, setOtpSessionId] = useState("");
  const [otpUserEmail, setOtpUserEmail] = useState("");
  const [otpMethod, setOtpMethod] = useState<'totp' | 'email'>('totp');

  // Enforced 2FA setup state
  const [showEnforcementDialog, setShowEnforcementDialog] = useState(false);
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [setupSessionId, setSetupSessionId] = useState("");

  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      window.history.replaceState({}, document.title);
    }

    // Check for account locked error in URL params
    const params = new URLSearchParams(location.search);
    if (params.get('error') === 'account_locked') {
      setError('Your account has been locked due to multiple failed login attempts. Please check your email for a password reset link or contact your administrator.');
      // Clear the error param from URL
      window.history.replaceState({}, document.title, location.pathname);
    }
  }, [location]);

  useEffect(() => {
    if (showSecondIdentifier && secondIdentifierInputRef.current) {
      // Small delay to allow animation to start before focusing
      setTimeout(() => {
        secondIdentifierInputRef.current?.focus();
      }, 100);
    }
  }, [showSecondIdentifier]);

  useEffect(() => {
    if (showPassword && passwordInputRef.current) {
      // Small delay to allow animation to start before focusing
      setTimeout(() => {
        passwordInputRef.current?.focus();
      }, 100);
    }
  }, [showPassword]);

  useEffect(() => {
    if (isAdminFlow && !showPassword && adminUserIdInputRef.current) {
      setTimeout(() => {
        adminUserIdInputRef.current?.focus();
      }, 100);
    }
  }, [isAdminFlow, showPassword]);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setEmail(newValue);
    // Only reset flow state when the user explicitly clears a previously-filled email
    // (not on initial empty value or browser autofill side effects).
    // We require the email field to be the active element to ensure this is a user-initiated clear.
    if (newValue === "" && email !== "" && document.activeElement === e.target) {
      setShowSecondIdentifier(false);
      setShowPassword(false);
      setSecondIdentifier("");
      setIsAdminFlow(false);
      setAdminUserId("");
      setAdminDetectedByUserId(false);
    }
  };

  const handleContinue = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setError("");
    if (!email) {
      setError("Please enter your email or user id");
      return;
    }

    // Validate email format if input contains '@' (indicating it's an email)
    if (isEmail(email)) {
      if (email.includes(' ')) {
        setError("Email address cannot contain spaces");
        return;
      }
      if (!isValidEmail(email)) {
        setError("Please enter a valid email address");
        return;
      }
    }

    // If Org ID required but not present, check
    if (showOrgId && !orgId) {
      setError("Please enter your Organization ID");
      return;
    }

    setIsSubmitting(true);

    try {
      // Use subdomain as tenant identifier if available
      const subdomain = getCurrentSubdomain();

      // Check authentication method using unique_id (and optional org_id or subdomain)
      const apiResult = await checkAuthMethod(email, orgId, subdomain || undefined);

      if (!apiResult.success) {
        // Handle Collision specifically — skip if subdomain already identifies the tenant
        if (apiResult.code === 'REQUIRE_ORG_ID' && !subdomain) {
          console.log('⚠️ Collision detected, requiring Org ID');
          setShowOrgId(true);
          setSuccessMessage("Multiple accounts found. Please enter your Organization ID.");
          setIsSubmitting(false);
          return;
        }

        // Show error message to the user (same as invalid password errors)
        console.log('⚠️ Auth method check failed:', apiResult.error);
        setError(apiResult.error || 'Failed to check authentication method');
        // Don't show any fields on error
      } else {
        // First-time user — needs to set password
        if (apiResult.route_to_reset_password) {
          const deliveryMethod = apiResult.delivery_method;
          console.log('⚠️ First-time user - delivery method:', deliveryMethod);

          if (deliveryMethod === 'email') {
            // Setup link sent to user's email
            setError('');
            setSuccessMessage(apiResult.message || "We've sent a password setup link to your email. Please check your inbox.");
          } else if (deliveryMethod === 'verification_required') {
            // No email — redirect to identity verification (DOB)
            navigate(`/verify-identity?uid=${encodeURIComponent(email)}`);
          } else if (deliveryMethod === 'contact_admin') {
            // No email, no DOB — must contact HR admin
            setError(apiResult.message || 'Please contact your HR admin to set up your account.');
          } else {
            setError('Please contact your HR admin to set up your account.');
          }

          setIsSubmitting(false);
          return;
        }

        // Admin user detected — switch to admin login flow
        if (apiResult.is_admin) {
          setIsAdminFlow(true);
          if (!isEmail(email)) {
            // Admin detected via admin_user_id — need email next in the second field
            setAdminDetectedByUserId(true);
          }
          setIsSubmitting(false);
          return;
        }

        // Success - check if we need a second identifier
        // This happens when:
        // 1. access_by = "BOTH" (need both email and user ID)
        // 2. access_by = "USERID" but user entered email (need user ID)
        // 3. access_by = "EMAIL" but user entered user ID (need email)
        const needsSecondIdentifier = apiResult.user_id || apiResult.email;

        if (needsSecondIdentifier) {
          // Detect if first input is email or userId
          setFirstInputIsEmail(isEmail(email));
          // Show second identifier field
          setShowSecondIdentifier(true);
        } else {
          // Show password field directly
          setShowPassword(true);
        }
      }
    } catch (err) {
      console.error('❌ Error checking auth method:', err);
      // Show error to user, don't show fields
      setError('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSecondIdentifierContinue = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setError("");
    if (!secondIdentifier) {
      setError("Please enter your email or user id");
      return;
    }
    // After second identifier is entered, show password field
    setShowPassword(true);
  };

  const handleAdminContinue = async () => {
    setError("");
    // Resolve which field holds admin_user_id and which holds email
    let resolvedAdminUserId: string;
    let resolvedEmail: string;

    if (adminDetectedByUserId) {
      // First field = admin_user_id, second field = email
      resolvedAdminUserId = email;
      resolvedEmail = adminUserId;
      if (!resolvedEmail) {
        setError("Please enter your email");
        return;
      }
    } else {
      // First field = email, second field = admin_user_id
      resolvedEmail = email;
      resolvedAdminUserId = adminUserId;
      if (!resolvedAdminUserId) {
        setError("Please enter your Admin User ID");
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const result = await adminCheckAuth(resolvedAdminUserId, resolvedEmail);
      if (!result.success) {
        setError(result.error || 'Failed to verify admin credentials');
      } else {
        setShowPassword(true);
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Step 1: Check email/userid via checkAuthMethod
    if (!isAdminFlow && !showSecondIdentifier && !showPassword) {
      handleContinue();
      return;
    }

    // Step 1b (admin): If admin flow and admin user ID not verified yet
    if (isAdminFlow && !showPassword) {
      handleAdminContinue();
      return;
    }

    // Step 2: If BOTH access_by, handle second identifier
    if (showSecondIdentifier && !showPassword) {
      handleSecondIdentifierContinue();
      return;
    }

    // Step 3: Handle password login
    if (!password) {
      setError("Please enter your password");
      return;
    }

    setError("");
    setIsSubmitting(true);

    try {
      // Admin login flow — use admin-specific API
      if (isAdminFlow) {
        // Resolve actual email: if admin_user_id was entered first, email is in adminUserId state
        const adminEmail = adminDetectedByUserId ? adminUserId : email;
        const result = await adminLoginWithCredentials(adminEmail, password);
        if (result.success) {
          // Only honor redirectParam if it's an admin route; otherwise default to admin home
          const target = redirectParam && redirectParam.startsWith('/tnadmin/') ? redirectParam : '/tnadmin/client';
          window.location.href = target;
        } else {
          setError(result.error || "Login failed");
        }
        return;
      }

      // Pass verification_id (secondIdentifier) only if it's a BOTH case
      const subdomain = getCurrentSubdomain();
      const result = await loginWithCredentials(
        email,
        password,
        secondIdentifier || undefined,
        undefined, // otp_code
        orgId || undefined, // org_id
        subdomain || undefined // domain (subdomain-based tenant resolution)
      );

      if (result.success) {
        console.log('✅ Login successful, redirecting...');
        const account = JSON.parse(localStorage.getItem('account') || '{}');
        if (getBaseDomain() && isMainDomain() && account.domain) {
          window.location.href = withTokenHandoff(buildSubdomainUrl(account.domain, '/home/my-portal'));
        } else {
          navigate('/home/my-portal', { replace: true });
        }
      } else {
        // Check if 2FA setup is required
        if ((result as any).code === '2FA_SETUP_REQUIRED') {
          const sessionId = localStorage.getItem('2fa_session_id') || (result as any).session_id || '';
          console.log('🔐 2FA setup required, showing enforcement dialog...');
          setSetupSessionId(sessionId);
          setShowEnforcementDialog(true);
          return;
        }
        // Check if 2FA verification is required (NEW FLOW - Show OTP Modal)
        if ((result as any).code === '2FA_VERIFICATION_REQUIRED') {
          console.log('🔐 2FA verification required, showing OTP modal...');
          // Store session_id and show OTP modal
          setOtpSessionId((result as any).session_id || '');
          setOtpUserEmail((result as any).user?.email || email);
          // Detect OTP method from response (default to 'totp' if not specified)
          const method = (result as any).otp_method || (result as any).otp_preference || 'totp';
          setOtpMethod(method === 'email' ? 'email' : 'totp');
          setShowOtpModal(true);
          return;
        }

        // Check for Force Reset
        if ((result as any).code === 'FORCE_RESET') {
          console.log('⚠️ Force password reset required');
          const userUuid = (result as any).user_uuid;
          navigate(`/auth/reset-password?uid=${userUuid}&force=true`);
          return;
        }

        // Check for contract/subscription expiry
        if ((result as any).code === 'ACCOUNT_RELATIONSHIP_INACTIVE') {
          setError("Your account plan has expired. Please contact your administrator.");
          return;
        }
        if ((result as any).code === 'SUBSCRIPTION_INVALID') {
          setError("Your subscription has expired or is not active. Please contact your administrator.");
          return;
        }

        setError(result.error || "Login failed");
      }
    } catch (err) {
      console.error('❌ Login error:', err);
      setError("Authentication failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleSSO = () => {
    console.log('🔵 Initiating Google SSO');
    loginWithGoogle(redirectParam);
  };

  const handleMicrosoftSSO = () => {
    console.log('🔵 Initiating Microsoft SSO');
    loginWithMicrosoft(redirectParam);
  };

  const handleOtpVerification = async (otpCode: string) => {
    try {
      const result = await verifyOtpWithSession(otpSessionId, otpCode);

      if (result.success) {
        console.log('✅ OTP verified successfully, redirecting...');
        setShowOtpModal(false);

        // Small delay to ensure token is set in API client before navigation
        await new Promise(resolve => setTimeout(resolve, 100));

        const account = JSON.parse(localStorage.getItem('account') || '{}');
        if (getBaseDomain() && isMainDomain() && account.domain) {
          window.location.href = withTokenHandoff(buildSubdomainUrl(account.domain, '/home/my-portal'));
        } else {
          navigate('/home/my-portal', { replace: true });
        }
        return { success: true };
      } else {
        console.error('❌ OTP verification failed:', result.error);
        return {
          success: false,
          error: result.error || 'Invalid OTP code. Please try again.',
        };
      }
    } catch (err) {
      console.error('❌ OTP verification error:', err);
      return {
        success: false,
        error: 'Verification failed. Please try again.',
      };
    }
  };

  const handleResendEmailOTP = async () => {
    try {
      console.log('📧 Resending email OTP...');
      const result = await resendEmailOTP(otpSessionId);

      if (result.success) {
        console.log('✅ Email OTP resent successfully');
        return { success: true };
      } else {
        console.error('❌ Failed to resend email OTP:', result.error);
        return {
          success: false,
          error: result.error || 'Failed to resend OTP. Please try again.',
        };
      }
    } catch (err: any) {
      console.error('❌ Resend email OTP error:', err);
      return {
        success: false,
        error: err.response?.data?.error || err.response?.data?.message || 'Failed to resend OTP. Please try again.',
      };
    }
  };

  const handleEnforcementProceed = () => {
    setShowEnforcementDialog(false);
    setShowSetupModal(true);
  };

  const handleEnforcedSetupComplete = (data: { access_token: string; refresh_token: string; user: any }) => {
    // Store tokens from the setup completion response
    if (data.access_token) {
      setStoredAccessToken(data.access_token);
    }
    if (data.refresh_token) {
      setStoredRefreshToken(data.refresh_token);
    }
    if (data.user) {
      localStorage.setItem('user', JSON.stringify(data.user));
    }

    // Clear temporary 2FA data
    localStorage.removeItem('2fa_session_id');
    localStorage.removeItem('2fa_user');

    setShowSetupModal(false);

    // Redirect — use window.location.href to ensure auth context reloads with new tokens
    const account = JSON.parse(localStorage.getItem('account') || '{}');
    const target = redirectParam && isValidRedirect(redirectParam) ? redirectParam : '/home/my-portal';
    if (getBaseDomain() && isMainDomain() && account.domain) {
      window.location.href = withTokenHandoff(buildSubdomainUrl(account.domain, target));
    } else {
      window.location.href = target;
    }
  };

  return (
    <div className="h-screen flex bg-white overflow-hidden">
      {/* Left Side - Login Form */}
      <div className="w-full lg:w-[45%] lg:max-w-[45vw] flex flex-col items-center justify-center px-6 sm:px-10 lg:px-16 xl:px-24 py-6 sm:py-8 lg:py-12 relative z-10">
        <div className="w-full max-w-md">
        <div className="mb-4 sm:mb-5 lg:mb-6">
          <div className="flex items-center mb-3 sm:mb-5 lg:mb-6">
            <img src={logo} alt="Trainery Logo" className="h-8 sm:h-10 lg:h-12" />
          </div>

          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-1 sm:mb-2 lg:mb-3">Login</h1>
          <p className="text-gray-500 text-sm sm:text-base leading-snug sm:leading-relaxed">
            One Trainery account for Everything eLearning and HR Solutions
          </p>
        </div>

        {successMessage && (
          <div className="mb-4 sm:mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md text-sm">
            {successMessage}
          </div>
        )}

        {error && (
          <div className="mb-4 sm:mb-6 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
            {error}
          </div>
        )}

        <div className="space-y-3 sm:space-y-4">
          <form onSubmit={handleSubmit} className="space-y-2 sm:space-y-3">
            <div className="space-y-1 sm:space-y-2">
              <Input
                id="email"
                name="username"
                type="text"
                autoComplete="username"
                placeholder="Enter your email or user id"
                value={email}
                onChange={handleEmailChange}
                className="h-10 sm:h-11 lg:h-12 px-3 sm:px-4 text-sm sm:text-base border-gray-200 focus:border-blue-600 focus:ring-blue-600 rounded-lg transition-all duration-200"
                required
                autoFocus
              />
            </div>

            {/* Org ID Field (for Employee Code collision) */}
            <div
              className={`transition-all duration-500 ease-in-out ${showOrgId ? 'max-h-[200px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
                }`}
            >
              <div className="space-y-1 sm:space-y-2 pt-1 sm:pt-2">
                <Input
                  id="orgId"
                  type="text"
                  placeholder="Enter your Organization ID"
                  value={orgId}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setOrgId(e.target.value)}
                  className="h-10 sm:h-11 lg:h-12 px-3 sm:px-4 text-sm sm:text-base border-gray-200 focus:border-blue-600 focus:ring-blue-600 rounded-lg transition-all duration-200"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {/* Admin second field: asks for email (if user_id entered first) or admin_user_id (if email entered first) */}
            <div
              className={`transition-all duration-500 ease-in-out ${isAdminFlow ? 'max-h-[200px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
                }`}
            >
              <div className="space-y-1 sm:space-y-2 pt-1 sm:pt-2">
                <Input
                  ref={adminUserIdInputRef}
                  id="adminSecondField"
                  name={adminDetectedByUserId ? "adminEmail" : "adminUserId"}
                  type="text"
                  autoComplete="off"
                  placeholder={adminDetectedByUserId ? "Enter your email" : "Enter your Admin User ID"}
                  value={adminDetectedByUserId ? adminUserId : adminUserId}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAdminUserId(e.target.value)}
                  className="h-10 sm:h-11 lg:h-12 px-3 sm:px-4 text-sm sm:text-base border-gray-200 focus:border-blue-600 focus:ring-blue-600 rounded-lg transition-all duration-200"
                  disabled={isSubmitting || showPassword}
                />
              </div>
            </div>

            {/* Second Identifier Field (for BOTH access_by) with Smooth Expansion Animation */}
            <div
              className={`transition-all duration-500 ease-in-out ${showSecondIdentifier ? 'max-h-[200px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
                }`}
            >
              <div className="space-y-1 sm:space-y-2 pt-1 sm:pt-2">
                <Input
                  ref={secondIdentifierInputRef}
                  id="secondIdentifier"
                  type="text"
                  placeholder={firstInputIsEmail ? "Enter your user id" : "Enter your email"}
                  value={secondIdentifier}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSecondIdentifier(e.target.value)}
                  className="h-10 sm:h-11 lg:h-12 px-3 sm:px-4 text-sm sm:text-base border-gray-200 focus:border-blue-600 focus:ring-blue-600 rounded-lg transition-all duration-200"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {/* Password Field with Smooth Expansion Animation */}
            <div
              className={`transition-all duration-500 ease-in-out ${showPassword ? 'max-h-[200px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
                }`}
            >
              <div className="space-y-1 sm:space-y-2 pt-1 sm:pt-2">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs sm:text-sm font-medium text-gray-700">Password</span>
                  <button
                    type="button"
                    onClick={() => navigate('/forgot-password', { state: { email } })}
                    className="text-xs sm:text-sm font-medium text-[#4F46E5] hover:underline"
                  >
                    Forgot password?
                  </button>
                </div>
                <div className="relative">
                  <Input
                    ref={passwordInputRef}
                    id="password"
                    name="password"
                    type={passwordVisible ? "text" : "password"}
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                    className="h-10 sm:h-11 lg:h-12 px-3 sm:px-4 text-sm sm:text-base border-gray-200 focus:border-blue-600 focus:ring-blue-600 rounded-lg pr-12"
                    disabled={isSubmitting}
                  />
                  <button
                    type="button"
                    onClick={() => setPasswordVisible(!passwordVisible)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                    tabIndex={-1} // Prevent tab focus interaction flow interruption
                  >
                    {passwordVisible ? (
                      <EyeSlash size={20} weight="regular" />
                    ) : (
                      <Eye size={20} weight="regular" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-10 sm:h-11 lg:h-12 text-sm sm:text-base font-semibold bg-[#4F46E5] hover:bg-[#4338CA] text-white rounded-lg shadow-lg shadow-indigo-200 transition-all duration-200 mt-1 sm:mt-2"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? (showPassword ? "Signing in..." : "Checking...")
                : (showPassword ? "Sign in" : "Continue")
              }
            </Button>
          </form>

          {!isAdminFlow && (
            <>
              <div className="relative py-1 sm:py-2">
                <div className="absolute inset-0 flex items-center">
                  <Separator className="w-full border-gray-100" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-4 text-gray-400 font-medium tracking-wider">Or</span>
                </div>
              </div>

              <div className="space-y-2 sm:space-y-3">
                <Button
                  variant="outline"
                  className="w-full h-10 sm:h-11 lg:h-12 text-sm sm:text-base font-medium border-gray-200 hover:bg-gray-50 hover:text-gray-900 text-gray-700 rounded-lg flex items-center justify-center gap-3"
                  onClick={handleMicrosoftSSO}
                  disabled={isSubmitting}
                >
                  <svg className="h-5 w-5" viewBox="0 0 24 24">
                    <path fill="#f25022" d="M1 1h10v10H1z" />
                    <path fill="#00a4ef" d="M13 1h10v10H1z" />
                    <path fill="#7fba00" d="M1 13h10v10H1z" />
                    <path fill="#ffb900" d="M13 13h10v10H13z" />
                  </svg>
                  Sign in with Microsoft
                </Button>

                <Button
                  variant="outline"
                  className="w-full h-10 sm:h-11 lg:h-12 text-sm sm:text-base font-medium border-gray-200 hover:bg-gray-50 hover:text-gray-900 text-gray-700 rounded-lg flex items-center justify-center gap-3"
                  onClick={handleGoogleSSO}
                  disabled={isSubmitting}
                >
                  <svg className="h-5 w-5 text-red-500" viewBox="0 0 24 24">
                    <path
                      fill="currentColor"
                      d="M12.545,10.239v3.821h5.445c-0.712,2.315-2.647,3.972-5.445,3.972c-3.332,0-6.033-2.701-6.033-6.032s2.701-6.032,6.033-6.032c1.498,0,2.866,0.549,3.921,1.453l2.814-2.814C17.503,2.988,15.139,2,12.545,2C7.021,2,2.543,6.477,2.543,12s4.478,10,10.002,10c8.396,0,10.249-7.85,9.426-11.748L12.545,10.239z"
                    />
                  </svg>
                  Sign in with Google
                </Button>
              </div>

              <div className="text-center text-xs sm:text-sm text-gray-600 mt-2 sm:mt-4 lg:mt-6">
                Don't have an account?{" "}
                <button
                  onClick={() => navigate("/register")}
                  className="font-medium text-[#4F46E5] hover:underline"
                >
                  Register here
                </button>
              </div>
            </>
          )}
        </div>
        </div>
      </div>

      {/* Right Side - Brand Panel */}
      <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden items-center justify-center p-6 xl:p-12 flex-col bg-gray-50 border-l border-gray-100">
        {/* Decorative gradient orbs */}
        <div className="absolute top-[-200px] right-[-100px] w-[500px] h-[500px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(79,70,229,0.06) 0%, transparent 60%)' }} />
        <div className="absolute bottom-[-150px] left-[-80px] w-[400px] h-[400px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(99,102,241,0.05) 0%, transparent 60%)' }} />

        <div className="relative z-10 w-full max-w-lg flex flex-col gap-4 xl:gap-8">
          {/* Tagline */}
          <div>
            <div className="w-10 h-0.5 bg-[#4F46E5] mb-4 xl:mb-6 rounded-full" />
            <p className="text-sm xl:text-lg text-indigo-500/80 font-medium tracking-wide mb-1 xl:mb-2">The complete</p>
            <h2 className="text-3xl xl:text-4xl font-bold text-gray-900 leading-tight tracking-tight">
              Talent Management
            </h2>
            <h2 className="text-3xl xl:text-4xl font-bold text-gray-900 leading-tight tracking-tight">
              Platform
            </h2>
            <p className="text-sm xl:text-base text-gray-500 mt-2 xl:mt-4 leading-relaxed">
              Three powerful modules. One unified platform.
            </p>
          </div>

          {/* Product module cards */}
          <div className="flex flex-col gap-3 xl:gap-4">
            {/* PerformSpark */}
            <div className="group flex items-center gap-4 xl:gap-5 bg-white rounded-2xl px-4 xl:px-6 py-4 xl:py-5 border border-gray-100 border-l-4 border-l-[#4F46E5] shadow-sm hover:shadow-md transition-all duration-300">
              <div className="w-24 xl:w-28 flex-shrink-0 flex items-center justify-center">
                <img src={performsparkLogo} alt="PerformSpark" className="h-5 xl:h-6 w-auto" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <h3 className="text-base xl:text-lg font-bold text-gray-900">PerformSpark</h3>
                  <a href="https://www.performspark.ai/" target="_blank" rel="noopener noreferrer" className="text-xs font-medium text-[#4F46E5]/60 tracking-wide group-hover:text-[#4F46E5] transition-colors">Explore &rarr;</a>
                </div>
                <p className="text-xs xl:text-sm text-gray-500 mt-1 xl:mt-1.5 leading-snug xl:leading-relaxed">
                  Drive performance with real-time goals, reviews & continuous feedback across your organization.
                </p>
              </div>
            </div>

            {/* TraineryLearn */}
            <div className="group flex items-center gap-4 xl:gap-5 bg-white rounded-2xl px-4 xl:px-6 py-4 xl:py-5 border border-gray-100 border-l-4 border-l-[#4F46E5] shadow-sm hover:shadow-md transition-all duration-300">
              <div className="w-24 xl:w-28 flex-shrink-0 flex items-center justify-center">
                <img src={traineryLearnLogo} alt="TraineryLearn" className="h-5 xl:h-6 w-auto" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <h3 className="text-base xl:text-lg font-bold text-gray-900">TraineryLearn</h3>
                  <span className="text-xs font-medium text-[#4F46E5]/60 tracking-wide group-hover:text-[#4F46E5] transition-colors">Explore &rarr;</span>
                </div>
                <p className="text-xs xl:text-sm text-gray-500 mt-1 xl:mt-1.5 leading-snug xl:leading-relaxed">
                  Deliver compliant training programs & track certifications at scale for regulated industries.
                </p>
              </div>
            </div>

            {/* CompBldr */}
            <div className="group flex items-center gap-4 xl:gap-5 bg-white rounded-2xl px-4 xl:px-6 py-4 xl:py-5 border border-gray-100 border-l-4 border-l-[#4F46E5] shadow-sm hover:shadow-md transition-all duration-300">
              <div className="w-24 xl:w-28 flex-shrink-0 flex items-center justify-center">
                <img src={compBldrLogo} alt="CompBldr" className="h-5 xl:h-6 w-auto" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <h3 className="text-base xl:text-lg font-bold text-gray-900">CompBldr</h3>
                  <a href="https://compbldr.ai/" target="_blank" rel="noopener noreferrer" className="text-xs font-medium text-[#4F46E5]/60 tracking-wide group-hover:text-[#4F46E5] transition-colors">Explore &rarr;</a>
                </div>
                <p className="text-xs xl:text-sm text-gray-500 mt-1 xl:mt-1.5 leading-snug xl:leading-relaxed">
                  Design competitive compensation structures with data-driven insights & pay equity analytics.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* OTP Verification Modal */}
      <OtpVerificationModal
        isOpen={showOtpModal}
        onClose={() => setShowOtpModal(false)}
        onVerify={handleOtpVerification}
        onResend={otpMethod === 'email' ? handleResendEmailOTP : undefined}
        userEmail={otpUserEmail}
        sessionId={otpSessionId}
        otpMethod={otpMethod}
      />

      {/* Enforced 2FA — Notification Dialog */}
      {showEnforcementDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            <div className="flex flex-col items-center text-center">
              <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <Shield className="w-7 h-7 text-blue-600" weight="fill" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Two-Factor Authentication Required
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                Your organization has enabled two-factor authentication for all accounts.
                You'll need to set up 2FA before you can access your account.
              </p>
              <Button
                onClick={handleEnforcementProceed}
                className="w-full"
              >
                Set Up Now
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Enforced 2FA — Setup Modal */}
      <TwoFactorSetupModal
        isOpen={showSetupModal}
        onClose={() => {}}
        onSetupComplete={() => {}}
        isEnforced={true}
        sessionId={setupSessionId}
        onEnforcedSetupComplete={handleEnforcedSetupComplete}
        userEmail={email}
      />
    </div>
  );
}
